/* ==========================================================================
   SHIPPING ESTIMATE
   Shows the real shipping cost on the product page and in the cart, quoted
   live from the store's shipping profiles, so nothing here needs editing
   when a rate or profile changes in Shopify admin.

   How: the Storefront API prices a throwaway cart (never the customer's own
   cart) to one address in each state and territory. If every state costs
   the same, one line is shown. If not, a state picker gives the exact rate.

   Self-contained like promo.js: no theme.js internals are relied on. Any
   failure leaves the "calculated at checkout" fallback text in place.
   ========================================================================== */
(function () {
  'use strict';

  // Tokenless Storefront API. Shopify falls forward to the oldest supported
  // version once this one retires, so the date does not need bumping.
  const ENDPOINT = '/api/2025-10/graphql.json';
  const CACHE_PREFIX = 'shippingEstimate:';
  const CACHE_MINUTES = 60;
  const STATE_KEY = 'shippingEstimateState';

  // One address per state and territory. Zones are state-level, so a capital
  // city stands in for the whole state. Postcode-level zones would not be
  // told apart by this.
  const STATES = [
    { code: 'ACT', city: 'Canberra', zip: '2600' },
    { code: 'NSW', city: 'Sydney', zip: '2000' },
    { code: 'NT', city: 'Darwin', zip: '0800' },
    { code: 'QLD', city: 'Brisbane', zip: '4000' },
    { code: 'SA', city: 'Adelaide', zip: '5000' },
    { code: 'TAS', city: 'Hobart', zip: '7000' },
    { code: 'VIC', city: 'Melbourne', zip: '3000' },
    { code: 'WA', city: 'Perth', zip: '6000' }
  ];

  const DELIVERY_FIELDS =
    'deliveryGroups(first: 10) { nodes { deliveryOptions { estimatedCost { amount } } } }';
  const CREATE =
    'mutation($input: CartInput!) { cartCreate(input: $input) { cart { id ' +
    DELIVERY_FIELDS +
    ' } userErrors { message } } }';
  const READ = 'query($id: ID!) { cart(id: $id) { ' + DELIVERY_FIELDS + ' } }';

  function storefront(query, variables) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query, variables: variables })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Storefront API ' + response.status);
        return response.json();
      })
      .then(function (json) {
        if (json.errors && json.errors.length) throw new Error(json.errors[0].message);
        return json.data;
      });
  }

  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  // Cheapest option in each delivery group, added up. Products on different
  // shipping profiles land in separate groups and their rates stack.
  // Returns null while Shopify is still working the rates out.
  function totalOf(cart) {
    const groups = cart && cart.deliveryGroups ? cart.deliveryGroups.nodes : [];
    if (!groups.length) return null;

    let total = 0;
    for (let i = 0; i < groups.length; i++) {
      const options = groups[i].deliveryOptions;
      if (!options.length) return null;
      total += Math.min.apply(
        null,
        options.map(function (option) {
          return parseFloat(option.estimatedCost.amount);
        })
      );
    }
    return total;
  }

  function quoteState(lines, state) {
    const input = {
      lines: lines,
      delivery: {
        addresses: [
          {
            selected: true,
            address: {
              deliveryAddress: {
                countryCode: 'AU',
                provinceCode: state.code,
                city: state.city,
                zip: state.zip
              }
            }
          }
        ]
      }
    };

    return storefront(CREATE, { input: input }).then(function (data) {
      const result = data.cartCreate;
      if (!result.cart) throw new Error(result.userErrors.map(function (e) { return e.message; }).join(', '));

      // Rates are sometimes not ready on the first response, so re-read briefly.
      function settle(cart, triesLeft) {
        const total = totalOf(cart);
        if (total !== null) return total;
        if (!triesLeft) throw new Error('No shipping rates for ' + state.code);
        return wait(700)
          .then(function () {
            return storefront(READ, { id: result.cart.id });
          })
          .then(function (read) {
            return settle(read.cart, triesLeft - 1);
          });
      }

      return settle(result.cart, 3);
    });
  }

  function readCache(key) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + key));
      if (cached && Date.now() - cached.at < CACHE_MINUTES * 60000) return cached.rates;
    } catch (e) {}
    return null;
  }

  function writeCache(key, rates) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ at: Date.now(), rates: rates }));
    } catch (e) {}
  }

  const pending = {};

  // Resolves to { ACT: 14.5, NSW: 14.5, ... } for the given cart lines.
  function quoteAll(lines) {
    const key = lines
      .map(function (line) {
        return line.merchandiseId.split('/').pop() + 'x' + line.quantity;
      })
      .sort()
      .join(',');

    const cached = readCache(key);
    if (cached) return Promise.resolve(cached);
    if (pending[key]) return pending[key];

    pending[key] = Promise.all(
      STATES.map(function (state) {
        return quoteState(lines, state);
      })
    )
      .then(function (totals) {
        const rates = {};
        STATES.forEach(function (state, index) {
          rates[state.code] = totals[index];
        });
        writeCache(key, rates);
        delete pending[key];
        return rates;
      })
      .catch(function (error) {
        delete pending[key];
        throw error;
      });

    return pending[key];
  }

  function money(amount) {
    if (amount === 0) return 'Free';
    return '$' + (amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2));
  }

  function savedState() {
    try {
      return localStorage.getItem(STATE_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  function saveState(code) {
    try {
      localStorage.setItem(STATE_KEY, code);
    } catch (e) {}
  }

  function line(variantId, quantity) {
    return { merchandiseId: 'gid://shopify/ProductVariant/' + variantId, quantity: quantity };
  }

  /* ------------------------------------------------------------------------
     <shipping-estimate data-context="product" data-variant-id="123">
     <shipping-estimate data-context="cart">
     The element's own markup is the fallback text. shipping-estimate.css
     keeps it out of sight while the quote loads, so it only ever shows if
     the quote fails or takes too long.
     ------------------------------------------------------------------------ */
  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      this.context = this.dataset.context || 'product';
      this.requestId = 0;

      if (this.context === 'cart') {
        this.onCartUpdated = this.quoteCart.bind(this);
        document.addEventListener('cart:updated', this.onCartUpdated);
        this.quoteCart();
      } else {
        this.onVariantChange = this.variantChanged.bind(this);
        document.addEventListener('variant:change', this.onVariantChange);
        this.quote([line(this.dataset.variantId, 1)]);
      }

      this.onStateChanged = this.render.bind(this);
      document.addEventListener('shipping-estimate:state', this.onStateChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
      document.removeEventListener('variant:change', this.onVariantChange);
      document.removeEventListener('shipping-estimate:state', this.onStateChanged);
    }

    variantChanged(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant || String(variant.id) === this.dataset.variantId) return;

      this.dataset.variantId = String(variant.id);
      this.quote([line(variant.id, 1)]);
    }

    quoteCart() {
      const self = this;
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) {
          return response.json();
        })
        .then(function (cart) {
          const lines = cart.items
            .filter(function (item) {
              return item.requires_shipping;
            })
            .map(function (item) {
              return line(item.variant_id, item.quantity);
            });

          if (!lines.length) {
            self.hidden = true;
            return;
          }
          self.hidden = false;
          self.quote(lines);
        })
        .catch(function () {
          self.classList.add('is-fallback');
        });
    }

    quote(lines) {
      const self = this;
      const requestId = ++this.requestId;

      quoteAll(lines)
        .then(function (rates) {
          if (requestId !== self.requestId) return;
          self.rates = rates;
          self.render();
        })
        .catch(function () {
          self.classList.add('is-fallback');
        });
    }

    render() {
      if (!this.rates) return;

      const rates = this.rates;
      const amounts = STATES.map(function (state) {
        return rates[state.code];
      });
      const lowest = Math.min.apply(null, amounts);
      const highest = Math.max.apply(null, amounts);

      this.textContent = '';
      this.classList.add('is-loaded');

      if (lowest === highest) {
        this.append(lowest === 0 ? 'Free ' : money(lowest) + ' flat rate ', this.policyLink('shipping'), ' Australia-wide');
      } else {
        const state = savedState();

        if (rates[state] !== undefined) {
          const amount = document.createElement('strong');
          amount.textContent = money(rates[state]);
          this.append(this.policyLink('Shipping'), ' to ', this.picker(state), ': ', amount);
        } else {
          this.append(this.policyLink('Shipping'), ' from ' + money(lowest) + '. Deliver to ', this.picker(''));
        }
      }

      if (this.context === 'cart') {
        const rest = document.createElement('span');
        rest.className = 'shipping-estimate__rest';
        rest.textContent = 'Taxes and discount codes calculated at checkout.';
        this.append(document.createElement('br'), rest);
      }
    }

    // The word "shipping" keeps its link to the shipping policy.
    policyLink(text) {
      if (!this.dataset.policyUrl) return text;

      const link = document.createElement('a');
      link.href = this.dataset.policyUrl;
      link.textContent = text;
      return link;
    }

    picker(selected) {
      const select = document.createElement('select');
      select.className = 'shipping-estimate__select';
      select.setAttribute('aria-label', 'Delivery state');

      if (!selected) {
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = 'Select state';
        select.append(placeholder);
      }

      STATES.forEach(function (state) {
        const option = document.createElement('option');
        option.value = state.code;
        option.textContent = state.code;
        select.append(option);
      });
      select.value = selected;

      select.addEventListener('change', function () {
        if (!select.value) return;
        saveState(select.value);
        document.dispatchEvent(new CustomEvent('shipping-estimate:state'));
      });

      return select;
    }
  }

  if (!customElements.get('shipping-estimate')) {
    customElements.define('shipping-estimate', ShippingEstimate);
  }
})();
